<template>
  <b-row>
    <b-col sm="12">
      <b-card body-class="py-2">
        <section class="list-view-heading">
          <ModuleViewHeading
            :module-id="access.module_id"
            :profile-id="access.profile_id"
            :reference="access.view_reference"
            summary-only
          />
          <div class="list-view-heading__controls">
            <div class="list-view-heading__actions">
              <TabHeaderActions />
            </div>
            <div class="list-view-heading__controls-end">
              <OrderCutsHelp :mode="mode" :status="cutStatus" />
              <b-button v-if="mode === 'list'"
                        variant="link"
                        class="list-view-icon-btn"
                        :class="{ 'list-view-icon-btn--active': filtersOpen }"
                        v-b-tooltip.hover.bottom
                        :title="filtersOpen ? 'Ocultar filtros' : 'Mostrar filtros'"
                        :aria-label="filtersOpen ? 'Ocultar filtros' : 'Mostrar filtros'"
                        @click="filtersOpen = !filtersOpen">
                <AppIcon name="filter" :fill="filtersOpen" />
              </b-button>
              <b-button v-if="mode === 'list'"
                        variant="link"
                        class="list-view-icon-btn list-view-icon-btn--primary"
                        v-b-tooltip.hover.bottom
                        title="Nuevo corte"
                        aria-label="Nuevo corte"
                        @click="mode = 'create'">
                <AppIcon name="add" />
              </b-button>
            </div>
          </div>
        </section>

        <b-collapse v-if="mode === 'list'" v-model="filtersOpen">
          <div class="list-view-toolbar__filters">
            <div class="list-view-filter" style="width: 120px;">
              <label class="filter-label">Nº corte</label>
              <b-form-input v-model.number="filters.cut_id" type="number" min="1" size="sm" placeholder="Todos" @keyup.enter.native="load(1)" />
            </div>
            <div class="list-view-filter" style="width: 150px;">
              <label class="filter-label">Desde</label>
              <b-form-input v-model="filters.date_start" type="date" size="sm" />
            </div>
            <div class="list-view-filter" style="width: 150px;">
              <label class="filter-label">Hasta</label>
              <b-form-input v-model="filters.date_end" type="date" size="sm" />
            </div>
            <div class="list-view-filter" style="flex: 1 1 170px; max-width: 220px;">
              <label class="filter-label">Estado</label>
              <b-form-select v-model="filters.status" :options="statusOptions" size="sm" />
            </div>
            <b-button variant="outline-dark" size="sm" title="Aplicar filtros" aria-label="Aplicar filtros"
                      style="height: 31px; width: 31px; padding: 0; flex-shrink: 0;" @click="load(1)">
              <AppIcon name="filter" />
            </b-button>
          </div>
        </b-collapse>
      </b-card>

      <OrderCutCreate v-if="mode === 'create'" :is-mobile="isMobileViewport" @cancel="mode = 'list'" @created="openCut" />

      <OrderCutWorkspace v-else-if="mode === 'cut'" :cut-id="cutId" :is-mobile="isMobileViewport"
                         @close="closeCut" @changed="cutsDirty = true" @deleted="closeCut(true)" @print="printCut"
                         @status="cutStatus = $event" />

      <b-card v-else>
        <b-table class="app-table"
                 responsive
                 :stacked="isMobileViewport ? 'md' : false"
                 sticky-header="60vh"
                 hover
                 small
                 :items="table.items"
                 :fields="fields"
                 :busy="table.loading"
                 :tbody-tr-class="rowClass"
                 @row-clicked="onRowClicked"
                 v-if="table.items.length || table.loading">
          <template v-slot:table-busy>
            <div class="text-center my-2"><b-spinner small class="align-middle" /> <strong>Cargando...</strong></div>
          </template>
          <template v-slot:cell(id)="data"><strong>Corte #{{ data.item.id }}</strong></template>
          <template v-slot:cell(cutoff_at)="data">{{ formatDate(data.item.cutoff_at) }}</template>
          <template v-slot:cell(created_at)="data">{{ formatDate(data.item.created_at) }}</template>
          <template v-slot:cell(orders_count)="data">{{ data.item.orders_count }}</template>
          <template v-slot:cell(invoices)="data">
            <span v-if="!data.item.invoices_done_count && !data.item.invoices_error_count" class="text-muted">—</span>
            <span v-else>
              {{ data.item.invoices_done_count }}/{{ data.item.orders_count }}
              <b-badge v-if="data.item.invoices_error_count" pill variant="danger" class="ml-1" :title="data.item.invoices_error_count + ' con error'">
                {{ data.item.invoices_error_count }} con error
              </b-badge>
            </span>
          </template>
          <template v-slot:cell(status)="data">
            <span class="order-cuts__status" :class="'order-cuts__status--' + data.item.status">{{ statusLabel(data.item.status) }}</span>
          </template>
          <template v-slot:cell(f_action)="data">
            <b-dropdown right no-caret size="sm" variant="link" boundary="window" toggle-class="app-table-action-toggle" class="pull-right">
              <template #button-content><AppIcon name="more_vert" label="Acciones" /></template>
              <b-dropdown-item @click="openCut(data.item.id)"><AppIcon name="visibility" /> Abrir</b-dropdown-item>
              <b-dropdown-item @click="printCut(data.item.id, 'order-cut-preparation')"><AppIcon name="print" /> Hoja de preparación (por producto)</b-dropdown-item>
              <b-dropdown-item @click="printCut(data.item.id, 'order-cut-orders')"><AppIcon name="summarize" /> Pedidos por vendedor y zona</b-dropdown-item>
            </b-dropdown>
          </template>
        </b-table>
        <EmptyState v-else
                    icon="inventory_2"
                    title="No hay cortes"
                    :description="hasFilters ? 'No hay cortes con esos filtros. Ampliá el rango de fechas para ver cortes anteriores.' : 'Creá un corte para enviar un lote de pedidos al depósito con el botón Agregar.'" />
        <div class="app-table-footer">
          <span class="list-view-toolbar__count">{{ table.total }} cortes</span>
        </div>
        <div class="list-view-footer">
          <b-button type="button" variant="outline-dark" size="sm" @click="$router.go(-1)">
            <AppIcon name="back" /> Volver
          </b-button>
          <nav v-if="table.total > table.perPage">
            <b-pagination class="mb-0" size="sm" pills :total-rows="table.total" :per-page="table.perPage" v-model="table.page" @input="load($event)" />
          </nav>
        </div>
      </b-card>

      <Printer v-if="printId" :key="printReference + printId" :reference="printReference" :registerID="printId" />
    </b-col>
  </b-row>
</template>

<script>
import serviceAPI from './services'
import Printer from '@/components/inc/printer/printer'
import Error from '@/handler/error'
import Modules from '@/config/modules'
import Profiles from '@/config/profiles'
import Helper from '@/handler/helper'
import TabHeaderActions from '@/components/inc/tabs/TabHeaderActions'
import OrderCutCreate from './inc/OrderCutCreate'
import OrderCutWorkspace from './inc/OrderCutWorkspace'
import OrderCutsHelp from './inc/OrderCutsHelp'

// Fecha local (YYYY-MM-DD) desplazada en días: toISOString() daría la fecha UTC.
function localDate(offsetDays) {
  const date = new Date()
  date.setDate(date.getDate() + offsetDays)
  return [date.getFullYear(), String(date.getMonth() + 1).padStart(2, '0'), String(date.getDate()).padStart(2, '0')].join('-')
}

const STATUS_LABELS = { preparing: 'En preparación', prepared: 'Preparado', ready: 'Listo para reparto', on_route: 'En reparto', finished: 'Finalizado' }

export default {
  components: { Printer, TabHeaderActions, OrderCutCreate, OrderCutWorkspace, OrderCutsHelp },
  data() {
    return {
      access: { module_id: Modules.PEDIDOS, profile_id: Profiles.PERSONAL, view_reference: 'orderCuts' },
      mode: 'list',
      cutId: null,
      cutsDirty: false,
      cutStatus: '',
      filtersOpen: false,
      filters: { cut_id: null, status: '', date_start: localDate(-7), date_end: localDate(0) },
      table: { items: [], page: 1, total: 0, perPage: 30, loading: false },
      printId: 0,
      printReference: 'order-cut-preparation',
      mobileMql: null,
      isMobileViewport: false,
      statusOptions: [
        { value: '', text: 'Todos' },
        { value: 'preparing', text: 'En preparación' },
        { value: 'prepared', text: 'Preparado' },
        { value: 'ready', text: 'Listo para reparto' },
      ],
      fields: [
        { key: 'id', label: 'Corte' },
        { key: 'cutoff_at', label: 'Pedidos hasta' },
        { key: 'created_at', label: 'Creado' },
        { key: 'orders_count', label: 'Pedidos', class: 'text-center' },
        { key: 'invoices', label: 'Facturación' },
        { key: 'status', label: 'Estado' },
        { key: 'f_action', label: '', class: 'text-right' },
      ],
    }
  },
  computed: {
    hasFilters() { return !!(this.filters.cut_id || this.filters.status || this.filters.date_start || this.filters.date_end) },
  },
  created() {
    Helper.hasAccessView(this.access)
    if(!Helper.showAccessView(this.access, this.access.view_reference)) return
    this.load(1)
    this.openFromRoute()
  },
  watch: {
    // El widget "Cortes en curso" del tablero de Pedidos abre un corte puntual.
    '$route.params.cutId'() { this.openFromRoute() },
  },
  mounted() {
    this.mobileMql = window.matchMedia('(max-width: 767.98px)')
    this.isMobileViewport = this.mobileMql.matches
    this.mobileMql.addEventListener('change', this.updateMobileViewport)
  },
  beforeDestroy() {
    if(this.mobileMql) this.mobileMql.removeEventListener('change', this.updateMobileViewport)
  },
  methods: {
    updateMobileViewport(event) { this.isMobileViewport = event.matches },
    statusLabel(status) { return STATUS_LABELS[status] || status },
    formatDate(value) {
      if(!value) return '—'
      const date = new Date(String(value).replace(' ', 'T'))
      return isNaN(date) ? value : date.toLocaleString('es-AR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })
    },
    async load(page = this.table.page) {
      this.table.loading = true
      try {
        const response = await serviceAPI.listarCortes({
          page,
          cut_id: this.filters.cut_id || null,
          status: this.filters.status || null,
          date_start: this.filters.date_start || null,
          date_end: this.filters.date_end || null,
        })
        const result = response.data || {}
        this.table.items = result.data || []
        this.table.total = result.total || 0
        this.table.perPage = result.per_page || 30
        this.table.page = result.current_page || page
      } catch(error) {
        this.$awn.alert(Error.showError(error))
      } finally {
        this.table.loading = false
      }
    },
    openFromRoute() {
      const id = Number(this.$route.params.cutId)
      if(id) this.openCut(id)
    },
    openCut(id) {
      this.cutId = Number(id)
      this.mode = 'cut'
      this.cutsDirty = true
    },
    closeCut() {
      this.mode = 'list'
      this.cutId = null
      if(this.cutsDirty) this.load()
      this.cutsDirty = false
    },
    printCut(id, reference = 'order-cut-preparation') {
      this.printId = 0
      this.$nextTick(() => {
        this.printReference = reference
        this.printId = id
      })
    },
    onRowClicked(item, index, event) {
      if(!item) return
      if(event && event.target && event.target.closest('button, a, .dropdown')) return
      this.openCut(item.id)
    },
    rowClass(item, type) {
      if(type !== 'row' || !item) return ''
      return item.invoices_error_count ? 'order-cuts__row--error' : ''
    },
  },
}
</script>

<style scoped>
.order-cuts__status { padding: 0.1rem 0.5rem; border-radius: 1rem; font-size: 0.72rem; font-weight: 700; white-space: nowrap; color: var(--app-color-text-secondary, #5d6872); background: var(--app-color-surface-subtle, #f1f3f5); }
.order-cuts__status--preparing { color: #8a5a00; background: var(--app-color-warning-soft, #fff8e6); }
.order-cuts__status--prepared { color: var(--app-color-primary, #2b5f8a); background: var(--app-color-primary-soft, #eef4fa); }
.order-cuts__status--ready, .order-cuts__status--on_route { color: var(--app-color-success, #1e7b4a); background: var(--app-color-success-soft, #eef8f2); }
.app-table >>> tbody tr { cursor: pointer; }
.app-table >>> tr.order-cuts__row--error td:first-child { box-shadow: inset 3px 0 var(--app-color-danger, #b42318); }
</style>
